"use client";

import { useState, useRef, useEffect } from "react";
import { User, LogOut, Heart, BarChart2, Settings, ChevronDown } from "lucide-react";
import { signOut } from "next-auth/react";
import Link from "next/link";

interface UserMenuProps {
    user: {
        name?: string | null;
        image?: string | null;
        email?: string | null;
        coins?: number;
        role?: string;
    };
}

export default function UserMenu({ user }: UserMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-3 hover:bg-white/5 p-2 rounded-lg transition-colors group"
            >
                <div className="hidden md:flex flex-col items-end leading-none">
                    <span className="text-sm font-medium text-white group-hover:text-[#00ff9d] transition-colors">
                        {user.name || "Hunter"}
                    </span>
                    <span className="text-[10px] font-mono text-[#00ff9d]">
                        {user.coins || 0} Coins
                    </span>
                </div>

                {user.image ? (
                    <img
                        src={user.image}
                        alt={user.name || "User"}
                        className="w-9 h-9 rounded-full border border-white/10 group-hover:border-[#00ff9d] transition-colors"
                    />
                ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center border border-white/10 group-hover:border-[#00ff9d] transition-colors">
                        <User className="w-5 h-5 text-gray-400" />
                    </div>
                )}

                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-[#0a0a0a] border border-white/10 rounded-xl shadow-2xl py-2 z-50 animate-in fade-in slide-in-from-top-2">
                    <div className="px-4 py-3 border-b border-white/5 mb-2">
                        <p className="text-sm font-bold text-white truncate">{user.name}</p>
                        <p className="text-xs text-gray-400 truncate">{user.email}</p>
                    </div>

                    <Link
                        href="/profile"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-[#00ff9d] transition-colors"
                        onClick={() => setIsOpen(false)}
                    >
                        <User className="w-4 h-4" />
                        My Profile
                    </Link>

                    {user.role === "ADMIN" && (
                        <Link
                            href="/admin"
                            className="flex items-center gap-2 px-4 py-2 text-sm text-purple-400 hover:bg-purple-500/10 transition-colors font-bold"
                            onClick={() => setIsOpen(false)}
                        >
                            <Settings className="w-4 h-4" />
                            Admin Dashboard
                        </Link>
                    )}

                    <Link
                        href="/profile/favorites"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-[#00ff9d] transition-colors"
                        onClick={() => setIsOpen(false)}
                    >
                        <Heart className="w-4 h-4" />
                        My Favorites
                    </Link>

                    <Link
                        href="/community/leaderboard"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-[#00ff9d] transition-colors"
                        onClick={() => setIsOpen(false)}
                    >
                        <BarChart2 className="w-4 h-4" />
                        Leaderboard
                    </Link>

                    <div className="my-2 border-t border-white/5"></div>

                    <button
                        onClick={() => signOut({ callbackUrl: "/" })}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors text-left"
                    >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                    </button>
                </div>
            )}
        </div>
    );
}
